import { createSelector, select } from '@ngrx/store';
import { getActiveExperienceCode } from '@ordersnproducts/modules/order-builder/reducers';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { BaseAsyncValue } from '@ordersnproducts/modules/shared/models/async-value';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { Observable } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { RecipeFormValues } from '../../../retail-specification/models/form-values';
import { SpecFieldNames } from '../../../specification/constants/specification-field-names.enum';
import { getRmsRecipeSpecifications, getRmsSpecificationsIsModify } from '../../selectors';
import { getOpenPlatformProducts } from '../../selectors/products.selectors';
import * as _ from 'lodash';

const productsAndRecipe = createSelector(
  getOpenPlatformProducts,
  getRmsRecipeSpecifications,
  getRmsSpecificationsIsModify,
  getActiveExperienceCode,
  (availableProducts, recipe, isModify, experienceCode) => {
    return {
      availableProducts,
      recipe,
      isModify,
      experienceCode,
    };
  },
);

export class ProductsOptionsLoaderOpenPlatformProvider extends BaseAsyncValue<SelectOptionI[]> {
  // Cache recipe size for performance
  // Trigger recipe size change only when needed
  private buffer = {
    lastFormSize: 0,
  };

  private availableProductsBff = [];

  getValue(): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(productsAndRecipe),
      takeUntil(this.ngDestroy),
      filter(({ availableProducts }) => availableProducts.length > 0),
      tap(({ availableProducts }) => {
        if (!_.isEqual(this.availableProductsBff, availableProducts)) {
          this.buffer.lastFormSize = 0;
        }
      }),
      filter(({ recipe }) => {
        const recipeSizeChanged = this.buffer.lastFormSize !== recipe.length;
        return recipeSizeChanged;
      }),
      tap(({ recipe, availableProducts }) => {
        this.buffer.lastFormSize = recipe.length;
        this.availableProductsBff = availableProducts;
      }),
      filter(({ recipe }) => recipe.length > 0),
      map(({ availableProducts, recipe, isModify, experienceCode }) => {
        if (
          this.isAttachedToMasterItem(recipe) &&
          !(
            isModify &&
            (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
              experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK)
          )
        ) {
          if (recipe.length > 1) {
            // There are more items in the recipe, lock the master first
            this.form.get(SpecFieldNames.PRODUCT_SELECTION).disable({ emitEvent: false });
            this.form.get(SpecFieldNames.CONTRACT_SELECT).disable({ emitEvent: false });
            this.form.get(SpecFieldNames.DATE) && this.form.get(SpecFieldNames.DATE).disable();
          } else {
            // Unlock otherwise
            this.form.get(SpecFieldNames.PRODUCT_SELECTION).enable();
            this.form.get(SpecFieldNames.CONTRACT_SELECT).enable();
            this.form.get(SpecFieldNames.DATE) && this.form.get(SpecFieldNames.DATE).enable();
          }

          // Regular case
          return availableProducts.map((product) => {
            return {
              id: product.productCode.replace(/^0+/, ''),
              name: `${product.productCode.replace(/^0+/, '')} / ${product.productDesc}`,
            };
          });
        } else {
          // Not a master item. Use master contract load strategy
          const masterItem = recipe[0];
          const masterContractCode = masterItem.values[SpecFieldNames.CONTRACT_SELECT];
          return availableProducts
            .filter((product) => {
              const productContracts = _safe(
                () => product.contracts.map((contract) => contract.contractCode.replace(/^0+/, '')),
                [],
              );
              return productContracts.includes(masterContractCode);
            })
            .map((product) => {
              return {
                id: product.productCode.replace(/^0+/, ''),
                name: `${product.productCode.replace(/^0+/, '')} / ${product.productDesc}`,
              };
            });
        }
      }),
    );
  }

  /**
   * Defines if current provider is attached to the master item of the recipe (First in index position)
   * @param recipe
   * @returns
   */
  private isAttachedToMasterItem(recipe: RecipeFormValues[]) {
    return this.mapItem.id === recipe[0].id;
  }
}
